import { HttpClient } from '@angular/common/http';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output
} from '@angular/core';

import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { debounceTime, delay, filter, finalize, switchMap, take, tap } from 'rxjs/operators';

import { ProductDetailService } from '@features/product-detail/product-detail.service';
import { ProductDetailViewModel } from '@models/api-search.models';
import { StoreViewModel } from '@models/api.models';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BasketCalculatedLineViewModel } from '@models/api-orders.models';
import { SettingsService } from '@core/services/settings.service';
import { UntypedFormControl } from '@angular/forms';
import { ReserveInStoreService } from '@features/reserve-in-store/reserve-in-store.service';
import { Router } from '@angular/router';
import { NotificationService } from '@core/services/notification/notification.service';
import { checkStoreStock } from '@features/store-stock/helper/check-store-stock';
import { BasketService } from '@core/services/basket/basket.service';
import { TranslationKey } from '@shared/translations/auto-generated/translation-key';
import { NotificationType } from '@core/services/notification/notification';
import { GtmService } from '@core/services/gtm/gtm.service';
import { ReserveInStore } from '@features/reserve-in-store/reserve-in-store';
import { StoreStock } from '@features/store-stock/store-stock-pipes/store-stock.pipe';
import { CanChangeStoreInReservationPipe } from '@features/reserve-in-store/reserve-in-store-pipes/can-change-store-in-reservation.pipe';
import { TranslateSafePipe } from '@shared/translate-safe/translate-safe.pipe';
import { StoreStockDetailedComponent } from '@features/store-stock/store-stock-detailed/store-stock-detailed.component';
import { QuantityComponent } from '@shared/form-elements/quantity/quantity.component';
import { ReserveInStoreLineComponent } from '@features/reserve-in-store/reserve-in-store-line/reserve-in-store-line.component';
import { AsyncPipe, NgClass, NgIf } from '@angular/common';
import { LoadingDotsComponent } from '@shared/loading-dots/loading-dots.component';

@UntilDestroy()
@Component({
    selector: 'app-reserve-in-store',
    templateUrl: './reserve-in-store.component.html',
    styleUrls: ['./reserve-in-store.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        LoadingDotsComponent,
        NgIf,
        NgClass,
        ReserveInStoreLineComponent,
        QuantityComponent,
        StoreStockDetailedComponent,
        AsyncPipe,
        TranslateSafePipe,
        CanChangeStoreInReservationPipe,
        StoreStock
    ]
})
export class ReserveInStoreComponent implements OnInit {
    private readonly reservationProductBs = new BehaviorSubject<BasketCalculatedLineViewModel | null>(null);
    private readonly pendingBs = new BehaviorSubject<boolean>(false);
    private product: ProductDetailViewModel;
    public reservationProduct$ = this.reservationProductBs.asObservable();
    public pending$ = this.pendingBs.asObservable();

    public quantity: UntypedFormControl;

    @Input()
    store: StoreViewModel;

    @Output()
    closeOverlay = new EventEmitter<void>();

    @Output()
    changeStore = new EventEmitter<void>();

    constructor(
        private readonly http: HttpClient,
        private readonly cd: ChangeDetectorRef,
        private readonly router: Router,
        private readonly gtmService: GtmService,
        private readonly basketService: BasketService,
        private readonly notificationService: NotificationService,
        public readonly reserveInStoreService: ReserveInStoreService,
        public readonly settingsService: SettingsService,
        public readonly productDetailService: ProductDetailService
    ) {
        this.quantity = new UntypedFormControl(1);

        this.basketService.basketActionEvent$.pipe(untilDestroyed(this)).subscribe(({ action }) => {
            this.pendingBs.next(false);
            if (action === 'add') {
                this.triggerNotification('success', 'general.feedback.notification.addToBasketSuccess');
                this.closeOverlay.emit();
            }
        });
    }

    ngOnInit(): void {
        this.quantity.valueChanges
            .pipe(debounceTime(100), untilDestroyed(this))
            .subscribe(() => this.calculateLinePrice());

        this.productDetailService.productDetail$.pipe(untilDestroyed(this)).subscribe(product => {
            if (product) {
                this.product = product;
                const { quantity = 1 } =
                    this.reserveInStoreService.getProductReservationQuantity(product.variantNo) || {};
                this.quantity.patchValue(quantity);
            }
        });

        if (this.store) {
            this.reserveInStoreService.addStoreToReservation(
                this.store.storeId?.toString() ?? '',
                this.store.storeName ?? '',
                this.store.reserveInStore ?? false
            );
        }
    }

    public calculateLinePrice(): void {
        this.pendingBs.next(true);
        this.reserveInStoreService
            .calculateLineReservation(
                { variantNo: this.product.variantNo, quantity: this.quantity.value },
                this.store.storeId?.toString() || ''
            )
            .pipe(
                finalize(() => this.pendingBs.next(false)),
                untilDestroyed(this)
            )
            .subscribe(res => {
                this.reservationProductBs.next(res.lines[0]);
                this.cd.markForCheck();
            });
    }

    public onFinalizeReservation = (): void => {
        this.pendingBs.next(true);

        // let's save the product in the reservation for the reservation flow and redirect the user to basket page
        this.saveProductToReserve()
            .pipe(
                switchMap(_ => this.settingsService.settings$),
                delay(1000),
                switchMap(({ pageReferences }) => this.router.navigate([pageReferences?.basketPage?.url])),
                take(1),
                untilDestroyed(this)
            )
            .subscribe(_ => {
                this.triggerNotification('success', 'reservation.notifications.addSuccessTitle');
                this.closeOverlay.emit();
            });
    };

    public onAddToBasket = (): void => {
        this.addProductDirectlyToBasket();
    };

    public onContinueShopping = (): void => {
        const isInStock = checkStoreStock(this.product.storeStocks, this.store) > 0;

        // In case we don't have the product in stock for the selected store,
        // we give the user the opportunity to cancel this action
        if (!isInStock) {
            this.closeOverlay.emit();
            return;
        }

        // let's save the product in the reservation for the reservation flow
        this.saveProductToReserve()
            .pipe(untilDestroyed(this))
            .subscribe(_ => {
                this.triggerNotification(
                    'success',
                    'reservation.notifications.addSuccessTitle',
                    'reservation.notifications.addSuccessText'
                );
                this.closeOverlay.emit();
            });
    };

    private addProductDirectlyToBasket(): void {
        const { variantNo, quantity = 1 } = this.reservationProductBs.getValue() || {};
        if (variantNo) {
            this.basketService.basketData$
                .pipe(
                    filter(({ requestInProgress }) => !requestInProgress),
                    take(1),
                    untilDestroyed(this)
                )
                .subscribe(() => {
                    this.pendingBs.next(true);
                    this.basketService.addToBasket(variantNo, quantity);
                });
        }
    }

    private saveProductToReserve(): Observable<ReserveInStore | undefined> {
        const stockStatus = checkStoreStock(this.product.storeStocks, this.store);
        if (stockStatus >= 1) {
            const product = this.reservationProductBs.getValue();

            const { quantity = 0 } =
                this.reserveInStoreService.getProductReservationQuantity(product?.variantNo || '') || {};
            const diff = (product?.quantity || 1) - quantity;

            // we only want to send to the reservation the difference between what is reserved and what we will reserve
            return this.reserveInStoreService.addProduct(product, diff || 1).pipe(
                tap(_ =>
                    this.gtmService.trackReserveInStoreClick({ ...this.product, quantity: diff }, this.store.storeName)
                ),
                untilDestroyed(this)
            );
        }

        // this should not be triggered, but it's here, just in case
        return throwError(new Error('not enough stock'));
    }

    private triggerNotification(type: NotificationType, headline: TranslationKey, text?: TranslationKey): void {
        this.notificationService.createNotification({
            type: type,
            translationHeadline: headline,
            translationText: text
        });
    }
}
